import { useEffect, useRef, useCallback } from 'react';
import { requestManager } from './requestManager';

/**
 * Hook that automatically resets currentPage state to 1 when non-page params change.
 * Returns a stable function to check if the next request should be skipped (silent page reset).
 * 
 * @param {Function} setCurrentPage - State setter for currentPage
 * @param {number} currentPage - Current page value
 * @param {string} requestKey - Key for the endpoint this screen fetches (e.g. "get:/api/v1/purchase-orders")
 * @returns {Function} shouldSkipRequest - Stable function that returns true if request should be skipped
 */
export const usePageReset = (setCurrentPage, currentPage, requestKey) => {
  const isUpdatingRef = useRef(false);
  const setCurrentPageRef = useRef(setCurrentPage);
  const currentPageRef = useRef(currentPage);
  const requestKeyRef = useRef(requestKey);

  // Keep refs updated
  useEffect(() => {
    setCurrentPageRef.current = setCurrentPage;
    currentPageRef.current = currentPage;
    requestKeyRef.current = requestKey;
  }, [setCurrentPage, currentPage, requestKey]);

  useEffect(() => {
    const unsubscribe = requestManager.onPageReset((emittedKey) => {
      // Only react to resets for this screen's endpoint
      if (!requestKeyRef.current || emittedKey !== requestKeyRef.current) return;
      // Prevent infinite loops: only update if page is not already 1
      // and we're not already in the middle of an update
      if (currentPageRef.current !== 1 && !isUpdatingRef.current) {
        isUpdatingRef.current = true;
        setCurrentPageRef.current(1);
        // Reset flag after state update completes
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      }
    });

    return unsubscribe;
  }, []); // Empty deps - only subscribe once

  /**
   * Check if the current page change is a silent (system-driven) reset.
   * If true, the data-fetching effect should skip the request.
   * This breaks the feedback loop: Request → Page Reset → State Change → (skip) → stable
   * 
   * Use useCallback to ensure stable reference so it doesn't trigger effect re-runs
   * Pass currentPage to validate the flag is still relevant
   */
  const shouldSkipRequest = useCallback(() => {
    return requestManager.checkAndClearSilentFlag(requestKeyRef.current, currentPageRef.current);
  }, []);

  return shouldSkipRequest;
};
